import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Asset, DashboardSummary, LightningFlash, Me, RoadClosure, SystemHealth, Tenant } from '@ww/shared';
import { ApiError, api, eventsUrl, getActingTenantId, setActingTenantId as storeActingTenantId } from '../api';

export type TenantInfo = DashboardSummary['tenant'];

export interface LiveContextValue {
  me: Me | null;
  /** true once the initial /api/auth/me call has settled */
  meLoaded: boolean;
  dashboard: DashboardSummary | null;
  health: SystemHealth | null;
  assets: Asset[] | null;
  /**
   * Operator-entered road closures for the acting tenant, live ones only.
   * Deliberately NOT on DashboardSummary: that object feeds the wallboard and shapes
   * the public alert view, and closures have no business widening it.
   */
  closures: RoadClosure[] | null;
  /**
   * Satellite total lightning for the acting tenant's area, last half hour.
   * Empty when LIGHTNING_ENABLED is off on the server, which is the default.
   */
  lightning: LightningFlash[] | null;
  /** Tenant currently being viewed (dashboard tenant, else the user's own). */
  tenant: TenantInfo | null;
  connected: boolean;
  lastError: string | null;
  lastUpdated: number | null;
  actingTenantId: string | null;
  setActingTenantId: (id: string | null) => void;
  /** All tenants (super_admin only). Shared so the header switcher and the admin page agree. */
  tenants: Tenant[] | null;
  refreshTenants: () => Promise<void>;
  refetchDashboard: () => Promise<void>;
  refetchHealth: () => Promise<void>;
  refetchAssets: () => Promise<void>;
  refetchClosures: () => Promise<void>;
  refetchLightning: () => Promise<void>;
  refetchAll: () => Promise<void>;
  refetchMe: () => Promise<Me | null>;
  login: (username: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
}

const LiveContext = createContext<LiveContextValue | null>(null);

const FALLBACK_POLL_MS = 60_000;
const MAX_BACKOFF_MS = 30_000;

export function LiveProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [closures, setClosures] = useState<RoadClosure[] | null>(null);
  const [lightning, setLightning] = useState<LightningFlash[] | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [actingTenantId, setActingState] = useState<string | null>(() => getActingTenantId());
  const [tenants, setTenants] = useState<Tenant[] | null>(null);

  const handleError = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.status === 401) {
      setMe(null);
      return;
    }
    setLastError(e instanceof Error ? e.message : String(e));
  }, []);

  const isSuper = me?.user.role === 'super_admin';
  const refreshTenants = useCallback(async () => {
    if (!isSuper) {
      setTenants(null);
      return;
    }
    try {
      const list = await api.tenants();
      setTenants(Array.isArray(list) ? list : []);
    } catch {
      setTenants([]);
    }
  }, [isSuper]);

  // Kept here rather than in the header, so creating a tenant on the admin page
  // refreshes the switcher too instead of leaving it stale until a page reload.
  useEffect(() => {
    void refreshTenants();
  }, [refreshTenants]);

  const refetchMe = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      return m;
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) handleError(e);
      setMe(null);
      return null;
    } finally {
      setMeLoaded(true);
    }
  }, [handleError]);

  const refetchDashboard = useCallback(async () => {
    try {
      const d = await api.dashboard();
      setDashboard(d);
      if (d?.health) setHealth(d.health);
      setLastUpdated(Date.now());
      setLastError(null);
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const refetchHealth = useCallback(async () => {
    try {
      setHealth(await api.health());
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const refetchAssets = useCallback(async () => {
    try {
      const list = await api.assets();
      setAssets(Array.isArray(list) ? list : []);
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const refetchClosures = useCallback(async () => {
    try {
      const list = await api.closures();
      setClosures(Array.isArray(list) ? list : []);
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const refetchLightning = useCallback(async () => {
    try {
      const r = await api.lightning();
      setLightning(r.flashes);
    } catch {
      /*
       * Silent on purpose, and NOT through handleError: lightning is the one optional
       * layer here, it is off by default, and a failure must not put an error banner
       * across a dashboard whose warnings and radar are working perfectly.
       */
      setLightning([]);
    }
  }, []);

  const refetchAll = useCallback(async () => {
    // Included here, not only on the SSE path, so the 60s fallback poll covers closures
    // and lightning when the event stream is down.
    await Promise.all([
      refetchDashboard(),
      refetchHealth(),
      refetchAssets(),
      refetchClosures(),
      refetchLightning(),
    ]);
  }, [refetchDashboard, refetchHealth, refetchAssets, refetchClosures, refetchLightning]);

  const setActingTenantId = useCallback((id: string | null) => {
    storeActingTenantId(id);
    setActingState(id);
    setDashboard(null);
    setAssets(null);
    setClosures(null);
    setLightning(null);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const m = await api.login(username, password);
    setMe(m);
    setMeLoaded(true);
    return m;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    storeActingTenantId(null);
    setActingState(null);
    setMe(null);
    setDashboard(null);
    setHealth(null);
    setAssets(null);
  }, []);

  // initial session probe
  useEffect(() => {
    void refetchMe();
  }, [refetchMe]);

  const userId = me?.user?._id ?? null;

  // data load + 60 s fallback polling
  useEffect(() => {
    if (!userId) return;
    void refetchAll();
    const t = window.setInterval(() => void refetchAll(), FALLBACK_POLL_MS);
    return () => window.clearInterval(t);
  }, [userId, actingTenantId, refetchAll]);

  // SSE with reconnect/backoff
  const fnRef = useRef({ refetchDashboard, refetchHealth, refetchAssets, refetchClosures, refetchLightning });
  fnRef.current = { refetchDashboard, refetchHealth, refetchAssets, refetchClosures, refetchLightning };
  useEffect(() => {
    if (!userId || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let retry: number | undefined;
    let backoff = 1000;
    let closed = false;

    const connect = () => {
      if (closed) return;
      es = new EventSource(eventsUrl(), { withCredentials: true });
      es.onopen = () => {
        backoff = 1000;
        setConnected(true);
      };
      const onData = () => {
        void fnRef.current.refetchDashboard();
        void fnRef.current.refetchAssets();
      };
      es.addEventListener('alerts', onData);
      es.addEventListener('assets', onData);
      es.addEventListener('status', () => void fnRef.current.refetchHealth());
      es.addEventListener('closures', () => void fnRef.current.refetchClosures());
      es.addEventListener('lightning', () => void fnRef.current.refetchLightning());
      es.onerror = () => {
        setConnected(false);
        es?.close();
        es = null;
        if (closed) return;
        retry = window.setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      };
    };
    connect();
    return () => {
      closed = true;
      if (retry) window.clearTimeout(retry);
      es?.close();
      setConnected(false);
    };
  }, [userId, actingTenantId]);

  const tenant: TenantInfo | null = dashboard?.tenant ?? me?.tenant ?? null;

  const value = useMemo<LiveContextValue>(
    () => ({
      me,
      meLoaded,
      dashboard,
      health,
      assets,
      closures,
      lightning,
      tenant,
      connected,
      lastError,
      lastUpdated,
      actingTenantId,
      setActingTenantId,
      tenants,
      refreshTenants,
      refetchDashboard,
      refetchHealth,
      refetchAssets,
      refetchClosures,
      refetchLightning,
      refetchAll,
      refetchMe,
      login,
      logout,
    }),
    [
      me,
      meLoaded,
      tenants,
      refreshTenants,
      dashboard,
      health,
      assets,
      closures,
      lightning,
      tenant,
      connected,
      lastError,
      lastUpdated,
      actingTenantId,
      setActingTenantId,
      refetchDashboard,
      refetchHealth,
      refetchAssets,
      refetchClosures,
      refetchLightning,
      refetchAll,
      refetchMe,
      login,
      logout,
    ],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveContextValue {
  const ctx = useContext(LiveContext);
  if (!ctx) throw new Error('useLive must be used inside <LiveProvider>');
  return ctx;
}
